import { JSX } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { GameNavigationTimer } from '@bg-components/GameNavigationTimer';
import { FeatureLabel, FeatureLabelTheme } from '@bg-components/FeatureLabel';
import { useStore } from '@betgames/bg-state-manager';
import {
    ClassicGameDefinition,
    t,
    GameId,
    routeUrlGenerator,
    useModalsManager,
    usePromotionState,
    iframeIntegration,
    gamesStatusEntity,
    gamesStatusSelectors,
    IPLSGameId,
    IMAGE_CONFIG,
    getBrandedConfig,
} from '@bg-shared';
import { Image } from '@bg-components/Image';
import { NonLiveLabel } from '@bg-components/NonLiveLabel';
import { NewLabelIcon } from '@bg-components/Icon';
import { ActiveBJSeats } from './components/ActiveBJSeats';
import { PLSBetLimits } from './components/PLSBetLimits';
import { IGameBrandedImage } from '../../../../../apps/iframe-server/src/interfaces';
import classes from './GameNavigationCard.module.scss';

interface IProps {
    gameId: GameId;
    className?: string;
    onClick?(): void;
}

export const GameNavigationCard = (props: IProps): JSX.Element => {
    const showNewLabel = ClassicGameDefinition.isNew(props.gameId);
    const modalManager = useModalsManager();
    const promotion = usePromotionState(props.gameId);
    const isPLSGame = ClassicGameDefinition.isPLSGame(props.gameId);
    const isMaintenance = useStore(
        gamesStatusEntity.store(props.gameId),
        gamesStatusSelectors.isMaintenance,
    );
    const brandedConfig = getBrandedConfig<IGameBrandedImage>(props.gameId);

    return (
        <Link
            onClick={() => {
                modalManager.closeAll();
                iframeIntegration.scrollToRelative();
                if (props.onClick) {
                    props.onClick();
                }
            }}
            data-qa={`area-game-card-${props.gameId}`}
            to={routeUrlGenerator.getGameUrl(props.gameId)}
            className={classNames(classes.container, props.className)}
        >
            <div data-qa="area-game-card-image" className={classes.imageContainer}>
                <Image
                    path={IMAGE_CONFIG.GAME_CARDS.path}
                    filename={ClassicGameDefinition.GAMES_NAMES[props.gameId]}
                    sizes={IMAGE_CONFIG.GAME_CARDS.sizes}
                    formats={IMAGE_CONFIG.GAME_CARDS.formats}
                    branding={brandedConfig?.card}
                    lazyLoad
                    alt={`navigation-card-${props.gameId}`}
                    className={classes.image}
                />
                {isPLSGame ? (
                    <ActiveBJSeats gameId={props.gameId as IPLSGameId} />
                ) : (
                    <GameNavigationTimer
                        dataQA="game-card-timer"
                        className={classes.timerLabel}
                        gameId={props.gameId}
                        showLiveIndicator={false}
                    />
                )}
                {isMaintenance && (
                    <div data-qa="text-game-card-maintenance" className={classes.maintenanceLabel}>
                        {t.string('maintenance')}
                    </div>
                )}
                {promotion.jackpot && (
                    <FeatureLabel
                        className={classes.jackpotLabel}
                        dataQa="text-game-menu-jackpot-label"
                        theme={FeatureLabelTheme.PROMO}
                    >
                        {t.string('jackpot')}
                    </FeatureLabel>
                )}
                <NonLiveLabel
                    gameId={props.gameId}
                    className={classes.nonLiveLabel}
                    dataQa="text-game-card-non-live-label"
                />

                {showNewLabel && (
                    <NewLabelIcon className={classes.newLabel} data-qa="text-game-menu-new-label" />
                )}

                {isPLSGame && <PLSBetLimits gameId={props.gameId} />}
            </div>
            <div data-qa="text-game-card-game-name" className={classes.title}>
                {t.gameName(props.gameId)}
            </div>
        </Link>
    );
};
